import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import comunEsAr from '@actualgold/shared/i18n/es-AR/comun.json';
import erroresEsAr from '@actualgold/shared/i18n/es-AR/errores.json';
import auditoriaEsAr from '@actualgold/shared/i18n/es-AR/auditoria.json';
import comunEn from '@actualgold/shared/i18n/en/comun.json';
import erroresEn from '@actualgold/shared/i18n/en/errores.json';
import auditoriaEn from '@actualgold/shared/i18n/en/auditoria.json';
import comunPtBr from '@actualgold/shared/i18n/pt-BR/comun.json';
import erroresPtBr from '@actualgold/shared/i18n/pt-BR/errores.json';
import auditoriaPtBr from '@actualgold/shared/i18n/pt-BR/auditoria.json';

/**
 * D5: ningún texto visible vive en el código. Los catálogos son del paquete compartido
 * porque el servidor también los necesita —para el correo, para los comprobantes— y no
 * puede haber dos versiones del mismo texto.
 *
 * El idioma de referencia es es-AR y no inglés: es el idioma en el que se piensa el
 * negocio, y traducir desde el original siempre sale mejor que traducir una traducción.
 */
export const IDIOMAS_DISPONIBLES = ['es-AR', 'en', 'pt-BR'] as const;
export type Idioma = (typeof IDIOMAS_DISPONIBLES)[number];

export const IDIOMA_POR_DEFECTO: Idioma = 'es-AR';
const CLAVE_GUARDADA = 'actualgold.idioma';

const recursos = {
  'es-AR': { comun: comunEsAr, errores: erroresEsAr, auditoria: auditoriaEsAr },
  en: { comun: comunEn, errores: erroresEn, auditoria: auditoriaEn },
  'pt-BR': { comun: comunPtBr, errores: erroresPtBr, auditoria: auditoriaPtBr },
} as const;

export function idiomaGuardado(): Idioma {
  const guardado = globalThis.localStorage?.getItem(CLAVE_GUARDADA);
  return IDIOMAS_DISPONIBLES.find((idioma) => idioma === guardado) ?? IDIOMA_POR_DEFECTO;
}

export function guardarIdioma(idioma: Idioma): void {
  globalThis.localStorage?.setItem(CLAVE_GUARDADA, idioma);
  document.documentElement.lang = idioma;
}

export async function iniciarI18n(idioma: Idioma = idiomaGuardado()): Promise<void> {
  // Volver a llamar con otro idioma cambia el idioma en lugar de reinicializar: i18next
  // ignora `lng` si ya está inicializado, y eso deja las pruebas contaminándose entre sí.
  if (i18next.isInitialized) {
    await i18next.changeLanguage(idioma);
    document.documentElement.lang = idioma;
    return;
  }

  await i18next.use(initReactI18next).init({
    resources: recursos,
    lng: idioma,
    fallbackLng: IDIOMA_POR_DEFECTO,
    ns: ['comun', 'errores', 'auditoria'],
    defaultNS: 'comun',
    interpolation: { escapeValue: false },
    // Un texto que falta tiene que verse en desarrollo, no pasar desapercibido.
    saveMissing: false,
    returnNull: false,
  });
  /**
   * `{{saldo, importe}}` muestra la plata como se mira, con dos decimales.
   *
   * El servidor manda los importes con cuatro, que es como se guardan y como hay que operarlos.
   * Sin esto, un error le dice al mostrador "Debe 43120.0000": se lee peor y no dice nada más.
   * Es conversión de presentación, así que vive acá y no en el catálogo ni en la API.
   *
   * Va por `services.formatter` y no por `interpolation.format`: esa segunda forma es la vieja
   * y esta versión de i18next la ignora **en silencio**, dejando el número tal cual. Se
   * descubrió mirando la pantalla, no leyendo la documentación.
   */
  i18next.services.formatter?.add('importe', (valor: unknown) => {
    if (typeof valor !== 'string') return String(valor);
    const [entera, fraccion = ''] = valor.split('.');
    return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
  });

  document.documentElement.lang = idioma;
}

export { i18next };
